import {useEffect} from "react";
import {useWatch} from "react-hook-form";
import {css} from "@emotion/react";

import {calculateRemSize, mb, p} from "@pg-design/helpers-css-strings";
import {IRange} from "@pg-design/range-module";
import {SliderWithRangeControlled} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {SearchFiltersRooms} from "../../../search/components/filters/SearchFiltersRooms";
import {FilterBudget} from "../../../search/components/generic_filters/FilterBudget";
import {usePriceRanges} from "../../../search/hooks/use_price_ranges";
import {stringifySearchFormArea} from "../../../search/utils/stringify_search_form_area";
import {stringifySearchFormRooms} from "../../../search/utils/stringify_search_form_rooms";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {ForYouBudgetFieldsNames} from "../../constants/for_you_budget_fields_names";
import {setForYouCriteria} from "../../state/for_you_modal_slice";
import {ForYouCriteriaGtmEvent, ForYouFormDisplayGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {ForYouFormValuesType} from "../../types/IForYouForm";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onChange?: (name: string, value: ForYouFormValuesType) => void;
}

export const ForYouCriteriaForm = (props: IProps) => {
    const modalState = useAppSelector((state) => state.forYouModal);
    const dispatch = useAppDispatch();
    const budgetFieldsNames = {
        radio: ForYouBudgetFieldsNames.RADIO,
        credit: ForYouBudgetFieldsNames.CREDIT,
        total: ForYouBudgetFieldsNames.TOTAL
    };
    const {isMobile} = useUserDevice();
    const rooms = useWatch({name: "rooms"});
    const area = useWatch({name: "area"});
    const budget = useWatch({name: budgetFieldsNames.total});
    const instalment = useWatch({name: budgetFieldsNames.credit});
    const radioWatcher = useWatch({name: budgetFieldsNames.radio});
    const maxArea = 300;
    const {maxPrice} = usePriceRanges();

    useEffect(() => {
        ForYouFormDisplayGtmEvent({
            event: ForYouEventType.CRITERIA,
            action: ForYouDisplayActions.OPEN,
            region: modalState.mainRegionName,
            openFrom: modalState.openFrom
        });
    }, []);

    const onSubmit = () => {
        const roomsString = stringifySearchFormRooms(rooms);
        const areaString = stringifySearchFormArea(area);
        const budgetString = radioWatcher === "total" ? budget.toString() : "";
        const instalmentString = radioWatcher === "monthly" ? instalment.toString() : "";
        dispatch(setForYouCriteria({rooms, area, ...(radioWatcher === "total" ? {budget} : {instalment})}));
        ForYouCriteriaGtmEvent({rooms: roomsString, area: areaString, budget: budgetString, instalment: instalmentString, openFrom: modalState.openFrom});
        props.onNextStep();
    };

    const setValue = (fieldName: string, value: IRange<number | "">) => {
        props.onChange?.(fieldName, value);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Pokoje
                </Text>

                <SearchFiltersRooms
                    toggleDropdown={() => null}
                    clearField={() => null}
                    name="rooms"
                    value={rooms}
                    showFooter={false}
                    onChange={setValue}
                    dataTestIdPrefix={FOR_YOU_TEST_IDS.ROOMS_SELECT_BUTTON}
                />
            </div>

            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Metraż
                </Text>

                <SliderWithRangeControlled
                    name="area"
                    min={0}
                    max={maxArea}
                    step={1}
                    inputsRightElement={
                        <span>
                            m<sup>2</sup>
                        </span>
                    }
                    dataTestIdLower={FOR_YOU_TEST_IDS.AREA_RANGE_LOWER}
                    dataTestIdUpper={FOR_YOU_TEST_IDS.AREA_RANGE_UPPER}
                />
            </div>

            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Budżet
                </Text>

                <FilterBudget
                    radioGroupClassName="radioGroupStyles"
                    radioClassName="radiosStyles"
                    inputVariant={isMobile ? "column" : "row"}
                    fieldNames={budgetFieldsNames}
                    maxPrice={maxPrice}
                />
            </div>

            <ForYouModalButton
                buttonText="Przejdź dalej"
                isDisabled={area.lower > area.upper || area.upper < area.lower || area.upper > maxArea}
                showStep
                dataTestId={FOR_YOU_TEST_IDS.NEXT_STEP_BUTTON}
            />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    background-color: #fff;
    ${p(3, 1.5, 0, 1.5)};

    .radiosStyles {
        flex: 0;
        min-width: 160px;
    }

    .radioGroupStyles {
        ${mb(0)};

        @media (min-width: ${getThemeBreakpoint().md}) {
            ${mb(2)};
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(4, 5, 4, 5)};
        height: 100%;
        background-color: ${getThemeVariable("colors.gray.100")};
    }

    .slider-with-range-slider-wrapper {
        flex: 1;
        width: 100%;
    }

    .slider-with-range-range-wrapper {
        flex: 1;
        width: 100%;

        @media (min-width: 1024px) {
            max-width: 40%;
        }
    }
`;

const filedWrapper = css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${calculateRemSize(1)};
    ${mb(5)};
`;
import {useEffect} from "react";
import {useWatch} from "react-hook-form";
import {css} from "@emotion/react";

import {calculateRemSize, mb, p} from "@pg-design/helpers-css-strings";
import {IRange} from "@pg-design/range-module";
import {SliderWithRangeControlled} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {SearchFiltersRooms} from "../../../search/components/filters/SearchFiltersRooms";
import {FilterBudget} from "../../../search/components/generic_filters/FilterBudget";
import {usePriceRanges} from "../../../search/hooks/use_price_ranges";
import {stringifySearchFormArea} from "../../../search/utils/stringify_search_form_area";
import {stringifySearchFormRooms} from "../../../search/utils/stringify_search_form_rooms";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {ForYouBudgetFieldsNames} from "../../constants/for_you_budget_fields_names";
import {setForYouCriteria} from "../../state/for_you_modal_slice";
import {ForYouCriteriaGtmEvent, ForYouFormDisplayGtmEvent} from "../../tracking/for_you_forms_gtm_events";
import {ForYouDisplayActions} from "../../types/ForYouDisplayActions";
import {ForYouEventType} from "../../types/ForYouEventType";
import {ForYouFormValuesType} from "../../types/IForYouForm";
import {SubmitHandlerType} from "../../types/SubmitHandlerType";
import {ForYouModalButton} from "../modal_parts/ForYouModalButton";

interface IProps {
    submitHandler: SubmitHandlerType;
    onNextStep: () => void;
    onChange?: (name: string, value: ForYouFormValuesType) => void;
}

export const ForYouCriteriaForm = (props: IProps) => {
    const modalState = useAppSelector((state) => state.forYouModal);
    const dispatch = useAppDispatch();
    const budgetFieldsNames = {
        radio: ForYouBudgetFieldsNames.RADIO,
        credit: ForYouBudgetFieldsNames.CREDIT,
        total: ForYouBudgetFieldsNames.TOTAL
    };
    const {isMobile} = useUserDevice();
    const rooms = useWatch({name: "rooms"});
    const area = useWatch({name: "area"});
    const budget = useWatch({name: budgetFieldsNames.total});
    const instalment = useWatch({name: budgetFieldsNames.credit});
    const radioWatcher = useWatch({name: budgetFieldsNames.radio});
    const maxArea = 300;
    const {maxPrice} = usePriceRanges();

    useEffect(() => {
        ForYouFormDisplayGtmEvent({
            event: ForYouEventType.CRITERIA,
            action: ForYouDisplayActions.OPEN,
            region: modalState.mainRegionName,
            openFrom: modalState.openFrom
        });
    }, []);

    const onSubmit = () => {
        const roomsString = stringifySearchFormRooms(rooms);
        const areaString = stringifySearchFormArea(area);
        const budgetString = radioWatcher === "total" ? budget.toString() : "";
        const instalmentString = radioWatcher === "monthly" ? instalment.toString() : "";
        dispatch(setForYouCriteria({rooms, area, ...(radioWatcher === "total" ? {budget} : {instalment})}));
        ForYouCriteriaGtmEvent({rooms: roomsString, area: areaString, budget: budgetString, instalment: instalmentString, openFrom: modalState.openFrom});
        props.onNextStep();
    };

    const setValue = (fieldName: string, value: IRange<number | "">) => {
        props.onChange?.(fieldName, value);
    };

    return (
        <form onSubmit={props.submitHandler(onSubmit)} css={formStyle}>
            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Pokoje
                </Text>

                <SearchFiltersRooms
                    toggleDropdown={() => null}
                    clearField={() => null}
                    name="rooms"
                    value={rooms}
                    showFooter={false}
                    onChange={setValue}
                    dataTestIdPrefix={FOR_YOU_TEST_IDS.ROOMS_SELECT_BUTTON}
                />
            </div>

            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Metraż
                </Text>

                <SliderWithRangeControlled
                    name="area"
                    min={0}
                    max={maxArea}
                    step={1}
                    inputsRightElement={
                        <span>
                            m<sup>2</sup>
                        </span>
                    }
                    dataTestIdLower={FOR_YOU_TEST_IDS.AREA_RANGE_LOWER}
                    dataTestIdUpper={FOR_YOU_TEST_IDS.AREA_RANGE_UPPER}
                />
            </div>

            <div css={filedWrapper}>
                <Text variant="body_copy_1" color={getThemeVariable("colors.gray.700")}>
                    Budżet
                </Text>

                <FilterBudget
                    radioGroupClassName="radioGroupStyles"
                    radioClassName="radiosStyles"
                    inputVariant={isMobile ? "column" : "row"}
                    fieldNames={budgetFieldsNames}
                    maxPrice={maxPrice}
                />
            </div>

            <ForYouModalButton
                buttonText="Przejdź dalej"
                isDisabled={area.lower > area.upper || area.upper < area.lower || area.upper > maxArea}
                showStep
                dataTestId={FOR_YOU_TEST_IDS.NEXT_STEP_BUTTON}
            />
        </form>
    );
};

const formStyle = css`
    width: 100%;
    background-color: #fff;
    ${p(3, 1.5, 0, 1.5)};

    .radiosStyles {
        flex: 0;
        min-width: 160px;
    }

    .radioGroupStyles {
        ${mb(0)};

        @media (min-width: ${getThemeBreakpoint().md}) {
            ${mb(2)};
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(4, 5, 4, 5)};
        height: 100%;
        background-color: ${getThemeVariable("colors.gray.100")};
    }

    .slider-with-range-slider-wrapper {
        flex: 1;
        width: 100%;
    }

    .slider-with-range-range-wrapper {
        flex: 1;
        width: 100%;

        @media (min-width: 1024px) {
            max-width: 40%;
        }
    }
`;

const filedWrapper = css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${calculateRemSize(1)};
    ${mb(5)};
`;
